import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import Header from "./components/Header";
import MainContent from "./components/MainContent";
import Footer from "./components/Footer";
import TermsOfUse from "./components/TermsOfUse";
import Contact from "./components/Contact";
import AboutFozzil from "./components/AboutFozzil";
import PrivacyPolicy from "./components/PrivacyPolicy";
import {
  EthicsAndCompliance,
  EthicsCompliance,
} from "./components/EthicsAndCompliance";
import AWM from "./components/AWM";
import ZYPHERAI from "./components/ZYPHERAI";
import FozzilIntelligence from "./components/FozzilIntelligence";
import Pricing from "./components/Pricing";
import NISTIA from "./components/NISTIA";
import PaymentPage from "./components/PaymentPage";
import CustomPaymentPage from "./components/CustomPaymentPage";
import PaymentSuccess from "./components/PaymentSuccess";
import TalkToFozzilButton from "./components/TalkToFozzilButton";
import Brand from "./components/Brand";
import AcceptableUsePolicy from "./components/AcceptableUsePolicy";
import CookiePolicy from "./components/CookiePolicy";
import Legal from "./components/Legal";
import ComingSoon from "./components/ComingSoon";
import CancellationAndRefund from "./components/CancellationAndRefund";
import Login from "./components/Login";
import HelpCenter from "./components/HelpCenter";
import SecurityAndPrivacy from "./components/SecurityAndPrivacy";

function AppContent() {
  const location = useLocation();
  const [showFozzilPillFromHero, setShowFozzilPillFromHero] = useState(false);
  const hideNavAndFooter =
    location.pathname === "/products/payment" ||
    location.pathname === "/pricing" ||
    location.pathname === "/payment" ||
    location.pathname === "/custom-payment";
  const hideFooter =
    location.pathname === "/products/pricing" ||
    location.pathname === "/pricing" ||
    location.pathname === "/payment" ||
    location.pathname === "/custom-payment" ||
    hideNavAndFooter;
  const companyPaths = [
    "/about",
    "/contact",
    "/privacy-policy",
    "/terms-of-use",
    "/ethics-compliance",
  ];
  const isCompanyPage = companyPaths.includes(location.pathname);
  const isHome = location.pathname === "/";
  const isPricingOrPayment =
    location.pathname === "/pricing" ||
    location.pathname === "/payment" ||
    location.pathname === "/custom-payment";
  const showFozzilPill =
    !isCompanyPage &&
    !isPricingOrPayment &&
    (!isHome || (isHome && showFozzilPillFromHero));

  // Footer intersection logic
  const footerRef = useRef(null);
  const [footerInView, setFooterInView] = useState(false);
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => setFooterInView(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (footerRef.current) observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="App">
      {!hideNavAndFooter && <Header />}
      <Routes>
        <Route
          path="/"
          element={
            <MainContent setShowFozzilPill={setShowFozzilPillFromHero} />
          }
        />
        <Route path="/about" element={<AboutFozzil />} />
        <Route path="/terms-of-use" element={<TermsOfUse />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/brand" element={<Brand />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/ethics-compliance"
          element={
            <>
              <EthicsAndCompliance />
              <EthicsCompliance />
            </>
          }
        />
        <Route path="/awm" element={<AWM />} />
        <Route path="/zypherai" element={<ZYPHERAI />} />
        <Route path="/fozzil-intelligence" element={<FozzilIntelligence />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/nistia" element={<NISTIA />} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="/custom-payment" element={<CustomPaymentPage />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route
          path="/acceptable-use-policy"
          element={<AcceptableUsePolicy />}
        />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/help-center" element={<HelpCenter />} />
        <Route path="/security-and-privacy" element={<SecurityAndPrivacy />} />
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route
          path="/cancellation-and-refund"
          element={<CancellationAndRefund />}
        />
      </Routes>
      {/* Only show the pill if the footer is NOT in view */}
      {showFozzilPill && !footerInView && <TalkToFozzilButton />}
      {!hideFooter && <Footer footerRef={footerRef} />}
    </div>
  );
}

function App() {
  return (
    <>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
      <style>{`
@import url("https://fonts.googleapis.com/css2?family=Cinzel:wght@400&family=Source+Sans+Pro:wght@300;400;600&display=swap");

:root {
  --primary-color: #000000;
  --secondary-color: #ffffff;
  --accent-color: #2196f3;
  --text-color: #333333;
  --font-primary: "Cinzel", serif;
  --font-secondary: "Source Sans Pro", sans-serif;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body,
html {
  background: #000 !important;
  font-family: var(--font-secondary);
  color: var(--text-color);
  line-height: 1.6;
}

.App {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

a {
  text-decoration: none;
  color: inherit;
}

ul {
  list-style: none;
}

img {
  max-width: 100%;
  height: auto;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.App-logo {
  height: 40vmin;
  pointer-events: none;
}

@media (prefers-reduced-motion: no-preference) {
  .App-logo {
    animation: App-logo-spin infinite 20s linear;
  }
}

.App-header {
  background-color: #282c34;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: calc(10px + 2vmin);
  color: black;
}

.App-link {
  color: #61dafb;
}

@keyframes App-logo-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
      `}</style>
    </>
  );
}

export default App;
